import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Checkbox from "../../atoms/checkbox";
import { useTermsAgreeStore } from "../../../lib/store/useTermsAgreeStore";
import { useTournamentTerms } from "../../../@apis/hooks/useTournament";
import { TOURNAMENT_LABEL } from "../../../lib/constants/common.constants";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { TournamentApplyInput } from "../../../types/tournament.types";
import { useModalStore } from "../../../lib/store/useModalStore";
import { cn } from "../../../lib/utils/cn";

interface Props {
  id: string | number;
  type?: "add" | "edit";
}

export default function CompetitionTermsScreen({ id }: Props) {
  const { data: termsData, isSuccess } = useQuery({
    ...useTournamentTerms(id),
    enabled: !!id,
  });
  const { data, success } = termsData || {};
  const { openModal } = useModalStore();

  const { control, setValue } = useFormContext<TournamentApplyInput>();

  // 선택 약관은 목록 최하단에 노출한다. 폼 인덱스도 이 순서를 따라야 하므로
  // 조회·전체동의·렌더링 모두 정렬된 목록을 사용한다.
  const termsList = useMemo(
    () =>
      [...(data?.termsList ?? [])].sort(
        (a, b) => Number(!!b.isRequired) - Number(!!a.isRequired),
      ),
    [data],
  );

  // 약관 모달 "동의" 신호 반영 (모달은 전역 렌더라 폼 컨텍스트 밖)
  const pendingAgreeId = useTermsAgreeStore((s) => s.pendingAgreeId);
  const clearPendingAgree = useTermsAgreeStore((s) => s.clear);
  useEffect(() => {
    if (pendingAgreeId == null) return;
    const index = termsList.findIndex((term) => term.id === pendingAgreeId);
    if (index >= 0) {
      setValue(
        `termAgreeList.${index}`,
        { termsId: pendingAgreeId, isAgreed: true },
        { shouldDirty: true, shouldValidate: true },
      );
    }
    clearPendingAgree();
  }, [pendingAgreeId, termsList, setValue, clearPendingAgree]);

  const agreeList = useWatch({ control, name: "termAgreeList" });
  const termsCount = termsList.length;
  const allChecked =
    termsCount > 0 &&
    (agreeList?.length ?? 0) === termsCount &&
    (agreeList ?? []).every((item) => item?.isAgreed);

  const toggleAll = () => {
    const next = !allChecked;
    termsList.forEach(({ id: termsId }, index) => {
      setValue(
        `termAgreeList.${index}`,
        { termsId, isAgreed: next },
        { shouldDirty: true, shouldValidate: true },
      );
    });
  };

  return (
    <div>
      <div className="space-y-[12px]">
        <h1 className="font-semibold">약관에 동의해 주세요.</h1>
        <p className="text-xs text-[#8e8e93]">
          대회 참가를 위해 아래 약관 동의가 필요해요.
        </p>
      </div>

      <div
        className="flex-row-between pb-[16px] border-b border-[#e6e6e6] cursor-pointer select-none mt-[36px]"
        role="button"
        tabIndex={0}
        onClick={toggleAll}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleAll();
          }
        }}
      >
        <Checkbox
          className="size-4"
          label="전체 동의"
          checked={allChecked}
          readOnly
        />
      </div>

      <div className="mt-[16px] space-y-[12px]">
        {isSuccess && success && (
          <>
            {termsList.map(({ id, type, term, isRequired }, index) => (
                <Controller
                  control={control}
                  key={id}
                  name={`termAgreeList.${index}`}
                  defaultValue={{ termsId: id, isAgreed: false }}
                  render={({ field }) => {
                    const toggleAgree = () => {
                      field.onChange({
                        ...field.value,
                        isAgreed: !field.value.isAgreed,
                      });
                    };
                    const openTermsModal = (e: React.MouseEvent) => {
                      e.stopPropagation();
                      openModal("TERMS_MODAL", {
                        html: term,
                        agreeTermsId: field.value.isAgreed ? undefined : id,
                      });
                    };
                    return (
                      <div
                        className="flex-row-between cursor-pointer select-none items-center gap-x-2"
                        role="button"
                        tabIndex={0}
                        onClick={toggleAgree}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleAgree();
                          }
                        }}
                      >
                        <Checkbox
                          className="size-4 "
                          label={
                            <p>
                              <span
                                className={cn(
                                  isRequired
                                    ? "text-[#ff3b30]"
                                    : "text-[#8e8e93]",
                                )}
                              >
                                {isRequired ? "[필수] " : "[선택] "}
                              </span>
                              {TOURNAMENT_LABEL[type]} 동의
                            </p>
                          }
                          checked={field.value.isAgreed}
                        />
                        <div
                          className="flex items-center font-semibold text-[#8e8e93] text-xs gap-x-[6px] shrink-0"
                          onClick={openTermsModal}
                        >
                          보기
                          <img
                            className="w-[6px] h-[14px] cursor-pointer"
                            src="/icons/ic-go.webp"
                            role="button"
                          />
                        </div>
                      </div>
                    );
                  }}
                ></Controller>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
